import React, { useEffect, useState } from 'react';
import { Avatar, hexAlpha, isHexColor } from '../../../components/ui';
import { AuthImage } from '../../../components/media/AuthImage';
import { InfluenceEntity } from './influenceModel';

export function useMediaQuery(query: string): boolean {
  const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

export const usePrefersReducedMotion = () => useMediaQuery('(prefers-reduced-motion: reduce)');

export const NEUTRAL = '#a3a3a3';
export const ACCENT = '#ea2a33';
export const INK = '#0a0a0a';

export const areaColor = (color?: string | null) => (isHexColor(color) ? color : NEUTRAL);

export const EntityAvatar: React.FC<{
  entity: Pick<InfluenceEntity, 'label' | 'type' | 'imageId'>;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ entity, size = 'sm', className = '' }) => (
  <Avatar
    name={entity.label}
    kind={entity.type}
    size={size}
    className={className}
    image={
      entity.imageId ? (
        <AuthImage mediaId={entity.imageId} alt="" className="h-full w-full object-cover" fallback={<span>{initials(entity.label)}</span>} />
      ) : undefined
    }
  />
);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

/** Thin score bar that animates its width; `ghost` marks a proposed value */
export const AnimatedBar: React.FC<{
  value: number;
  color?: string | null;
  ghost?: number | null;
  className?: string;
  height?: string;
}> = ({ value, color, ghost, className = '', height = 'h-1.5' }) => {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setShown(value));
    return () => window.cancelAnimationFrame(id);
  }, [value]);
  const c = areaColor(color);
  const pct = Math.max(0, Math.min(100, shown));
  return (
    <span className={`relative block w-full overflow-hidden rounded-full bg-neutral-100 ${height} ${className}`} aria-hidden="true">
      {ghost != null && (
        <span
          className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out motion-reduce:transition-none"
          style={{
            width: `${Math.max(0, Math.min(100, ghost))}%`,
            backgroundImage: `repeating-linear-gradient(135deg, ${hexAlpha(c, 0.45)} 0 2px, transparent 2px 4px)`,
          }}
        />
      )}
      <span
        className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
        style={{ width: `${pct}%`, backgroundColor: c }}
      />
    </span>
  );
};

export function relativeTime(iso?: string | null, now: Date = new Date()): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const days = Math.floor((now.getTime() - t) / 86400000);
  if (days < 1) return 'today';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30.4);
  if (months < 12) return `${months} mo ago`;
  const years = Math.floor(months / 12);
  return `${years} yr${years > 1 ? 's' : ''} ago`;
}

export const PanelSection: React.FC<{
  title: React.ReactNode;
  aside?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, aside, className = '', children }) => (
  <section className={className}>
    <div className="mb-2 flex min-h-[24px] items-center justify-between gap-2">
      <h3 className="text-[12px] font-normal text-neutral-500">{title}</h3>
      {aside && <div className="flex items-center gap-1 text-[12px] text-neutral-400">{aside}</div>}
    </div>
    {children}
  </section>
);

/** Stacked share bar: top actors in the area colour (fading), the rest neutral */
export const ShareBar: React.FC<{
  shares: Array<{ label: string; share: number }>;
  color?: string | null;
  className?: string;
}> = ({ shares, color, className = '' }) => {
  const c = areaColor(color);
  const alphas = [1, 0.72, 0.52, 0.38, 0.27];
  const used = shares.reduce((a, s) => a + s.share, 0);
  return (
    <span className={`flex h-1.5 w-full gap-px overflow-hidden rounded-full bg-neutral-100 ${className}`}>
      {shares.map((s, i) => (
        <span
          key={`${s.label}-${i}`}
          title={`${s.label} · ${Math.round(s.share * 100)}% of influence weight`}
          className="h-full transition-[width] duration-700 ease-out first:rounded-l-full motion-reduce:transition-none"
          style={{ width: `${s.share * 100}%`, backgroundColor: hexAlpha(c, alphas[i] ?? 0.2) }}
        />
      ))}
      {used < 0.999 && <span className="h-full flex-1" title={`Others · ${Math.round((1 - used) * 100)}%`} />}
    </span>
  );
};

export const DeltaPill: React.FC<{ delta: number | null; className?: string }> = ({ delta, className = '' }) => {
  if (delta == null) {
    return <span className={`inline-flex h-5 items-center rounded-full bg-sky-50 px-1.5 text-[11px] font-medium text-sky-700 ${className}`}>New</span>;
  }
  const tone = delta > 0 ? 'bg-emerald-50 text-emerald-700' : delta < 0 ? 'bg-primary-50 text-primary-700' : 'bg-neutral-100 text-neutral-500';
  return (
    <span className={`inline-flex h-5 min-w-[34px] items-center justify-center rounded-full px-1.5 text-[11px] font-medium tabular-nums ${tone} ${className}`}>
      {delta > 0 ? '+' : delta < 0 ? '−' : '±'}
      {Math.abs(delta)}
    </span>
  );
};

/** Local keyframes (the shared Tailwind config is not ours to change) */
export const InfluenceStyles: React.FC = () => (
  <style>{`
@keyframes infl-slide-right { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
@keyframes infl-slide-left { from { opacity: 0; transform: translateX(-16px); } to { opacity: 1; transform: none; } }
@keyframes infl-pop { from { opacity: 0; transform: scale(0.2); } to { opacity: 1; transform: scale(1); } }
@keyframes infl-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes infl-rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes infl-draw { from { stroke-dashoffset: var(--infl-len, 400); } to { stroke-dashoffset: 0; } }
@keyframes infl-pulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
.infl-slide-right { animation: infl-slide-right 320ms cubic-bezier(0.16, 1, 0.3, 1) both; }
.infl-slide-left { animation: infl-slide-left 320ms cubic-bezier(0.16, 1, 0.3, 1) both; }
.infl-pop { animation: infl-pop 520ms cubic-bezier(0.34, 1.36, 0.64, 1) both; transform-box: fill-box; transform-origin: center; }
.infl-fade { animation: infl-fade 400ms ease-out both; }
.infl-rise { animation: infl-rise 260ms ease-out both; }
.infl-pulse { animation: infl-pulse 1.6s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .infl-slide-right, .infl-slide-left, .infl-pop, .infl-fade, .infl-rise, .infl-pulse { animation: none !important; }
}
`}</style>
);
